import { Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Checkbox, ListItemText, useTheme } from '@mui/material';
import { useEffect, useId, useMemo, useState } from 'react';
import { selectComponentStyles, selectMenuProps } from './SelectComponent.styles';

export type SelectOptionId = string | number;

export interface SelectOption<TId extends SelectOptionId = SelectOptionId> {
  id: TId;
  name: string;
}

interface SelectComponentBaseProps<TId extends SelectOptionId> {
  options: SelectOption<TId>[];
  label?: string;
  disabled?: boolean;
  placeholder?: string;
  fontSize?: string;
  onClose?: () => void;
}

interface SingleSelectProps<TId extends SelectOptionId> {
  multiple?: false;
  value?: TId;
  onChange: (selectedId: TId) => void;
}

interface MultiSelectProps<TId extends SelectOptionId> {
  multiple: true;
  value?: TId[];
  onChange?: (selectedIds: TId[]) => void;
  onCloseChange?: (selectedIds: TId[]) => void;
  allOptionLabel?: string;
  allOptionId?: TId;
}

type SelectComponentProps<TId extends SelectOptionId> = SelectComponentBaseProps<TId> & (
  SingleSelectProps<TId> | MultiSelectProps<TId>
);

export const ALL_OPTION_ID = 'ALL';
export const ALL_OPTION_NUMBER_ID = -1;

function resolveAllOptionId<TId extends SelectOptionId>(
  allOptionLabel: string | undefined,
  allOptionId: TId | undefined,
  hasNumericOptionIds: boolean
): TId | undefined {
  if (!allOptionLabel) {
    return undefined;
  }
  if (allOptionId !== undefined) {
    return allOptionId;
  }
  return (hasNumericOptionIds ? ALL_OPTION_NUMBER_ID : ALL_OPTION_ID) as TId;
}

export const SelectComponent = <TId extends SelectOptionId>(props: SelectComponentProps<TId>) => {
  const { options, label = 'Select', disabled = false, placeholder, fontSize, onClose } = props;
  const multiple = props.multiple === true;
  const value = props.value;
  const theme = useTheme();
  const resolvedFontSize = fontSize || theme.typography.body1.fontSize;
  const selectId = useId();
  const labelId = `${selectId}-label`;
  const allOptionLabel = multiple ? props.allOptionLabel : undefined;
  const allOptionName = allOptionLabel ? `All ${allOptionLabel}` : '';
  const hasNumericOptionIds = options.length > 0 && options.every((option) => typeof option.id === 'number');
  const resolvedAllOptionId = resolveAllOptionId(
    allOptionLabel,
    multiple ? props.allOptionId : undefined,
    hasNumericOptionIds
  );

  const hasAllOptionAlready = !!resolvedAllOptionId && options.some(
    (option) => option.id === resolvedAllOptionId || option.name === allOptionName
  );
  const shouldAddAllOption = multiple
    && !!allOptionLabel
    && resolvedAllOptionId !== undefined
    && !hasAllOptionAlready;

  const renderedOptions = shouldAddAllOption
    ? [{ id: resolvedAllOptionId, name: allOptionName }, ...options]
    : options;
  const closeChangeHandler = multiple ? props.onCloseChange : undefined;
  const usesCloseChange = multiple && typeof closeChangeHandler === 'function';

  const [draftMultipleValue, setDraftMultipleValue] = useState<TId[]>(
    multiple && Array.isArray(value) ? value : []
  );
  useEffect(() => {
    if (multiple) {
      setDraftMultipleValue(Array.isArray(value) ? value : []);
    }
  }, [multiple, value]);

  const effectiveMultipleValue: TId[] = useMemo(() => {
    if (!multiple) {
      return [];
    }
    if (usesCloseChange) {
      return draftMultipleValue;
    }
    return Array.isArray(value) ? value : [];
  }, [draftMultipleValue, multiple, usesCloseChange, value]);
  const resolveIdFromRawValue = (rawValue: string | number): TId => {
    const matchedOption = renderedOptions.find((option) => String(option.id) === String(rawValue));
    if (matchedOption) {
      return matchedOption.id;
    }
    if (hasNumericOptionIds) {
      return Number(rawValue) as TId;
    }
    return String(rawValue) as TId;
  };
  
  const selectedIdsForRender: TId[] = (() => {
    if (!multiple) {
      return [];
    }
    if (resolvedAllOptionId !== undefined && effectiveMultipleValue.includes(resolvedAllOptionId)) {
      return [resolvedAllOptionId];
    }
    return effectiveMultipleValue;
  })();
  const toSelectedIdArray = (selectedValue: SelectOptionId | SelectOptionId[]): SelectOptionId[] => {
    if (typeof selectedValue === 'string') {
      return selectedValue.split(',');
    }
    if (Array.isArray(selectedValue)) {
      return selectedValue;
    }
    return [selectedValue];
  };

  const handleChange = (event: SelectChangeEvent<SelectOptionId | SelectOptionId[]>) => {
    const selectedValue = event.target.value;

    if (props.multiple === true) {
      const selectedIds = toSelectedIdArray(selectedValue);
      const normalizedSelectedIds = selectedIds.map((selectedId) => resolveIdFromRawValue(selectedId));
      const hasAllOption = resolvedAllOptionId !== undefined;
      if (!hasAllOption) {
        if (usesCloseChange) {
          setDraftMultipleValue(normalizedSelectedIds);
        } else {
          props.onChange?.(normalizedSelectedIds);
        }
        return;
      }

      const includesAll = normalizedSelectedIds.includes(resolvedAllOptionId);
      const hasOtherSelections = normalizedSelectedIds.some((id) => id !== resolvedAllOptionId);
      if (includesAll && hasOtherSelections) {
        // Keep "All" mutually exclusive: selecting it clears others, selecting another item while "All" is active removes "All".
        if (selectedIdsForRender.includes(resolvedAllOptionId)) {
          const nextSelectedIds = normalizedSelectedIds.filter((id) => id !== resolvedAllOptionId);
          if (usesCloseChange) {
            setDraftMultipleValue(nextSelectedIds);
          } else {
            props.onChange?.(nextSelectedIds);
          }
          return;
        }
        if (usesCloseChange) {
          setDraftMultipleValue([resolvedAllOptionId]);
        } else {
          props.onChange?.([resolvedAllOptionId]);
        }
        return;
      }
      if (usesCloseChange) {
        setDraftMultipleValue(normalizedSelectedIds);
      } else {
        props.onChange?.(normalizedSelectedIds);
      }
      return;
    }

    props.onChange(resolveIdFromRawValue(selectedValue as SelectOptionId));
  };

  const handleClose = () => {
    if (usesCloseChange) {
      closeChangeHandler?.(selectedIdsForRender);
    }
    onClose?.();
  };

  const renderValue = (selected: SelectOptionId | SelectOptionId[]) => {
    if (multiple) {
      const selectedArray = Array.isArray(selected) ? selected : [selected];
      const normalizedSelectedArray = selectedArray.map((selectedId) => resolveIdFromRawValue(selectedId));
      const normalizedSelectedWithAllApplied = resolvedAllOptionId !== undefined && normalizedSelectedArray.includes(resolvedAllOptionId)
        ? [resolvedAllOptionId]
        : normalizedSelectedArray;
      if (selectedArray.length === 0) {
        return placeholder || '';
      }
      return normalizedSelectedWithAllApplied
        .map((id) => renderedOptions.find((opt) => opt.id === id)?.name)
        .filter(Boolean)
        .join(', ');
    } else {
      const selectedId = selected;
      const option = renderedOptions.find((opt) => opt.id === selectedId);
      return option?.name || placeholder || '';
    }
  };

  return (
    <FormControl fullWidth disabled={disabled} sx={selectComponentStyles.formControl(theme, resolvedFontSize)}>
      <InputLabel id={labelId} shrink={placeholder ? true : undefined}>{label}</InputLabel>
      <Select
        labelId={labelId}
        id={selectId}
        multiple={multiple}
        displayEmpty={Boolean(placeholder)}
        value={multiple
          ? effectiveMultipleValue
          : (value !== undefined ? value : ('' as TId))}
        onChange={handleChange}
        onClose={handleClose}
        renderValue={renderValue}
        label={label}
        sx={selectComponentStyles.select(theme, resolvedFontSize)}
        MenuProps={selectMenuProps(theme)}
      >
        {renderedOptions.map((option) => (
          <MenuItem key={option.id} value={option.id} sx={selectComponentStyles.menuItem(theme, resolvedFontSize)}>
            {multiple && (
              <Checkbox
                checked={selectedIdsForRender.includes(option.id)}
                sx={selectComponentStyles.checkbox(theme)}
              />
            )}
            <ListItemText primary={option.name} sx={selectComponentStyles.listItemText(theme, resolvedFontSize)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SelectComponent;
